import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CrmAccountFacts } from '@/components/accounts/CrmAccountFacts';
import { RowCrmAction } from '@/components/views/atoms/RowCrmAction';
import { readAppRecordAccount, type AppRecordAccount as Account } from '@/lib/app-record-account';
import { useCrmAccount } from '@/lib/use-crm-account';

const noop = () => undefined;

/** HubSpot facts of the linked account, read like on the account page (R2). */
const LinkedAccount = ({ account }: { account: Extract<Account, { status: 'found' }> }) => {
  const crm = useCrmAccount(account.subjectId, noop);
  return <>
    <p className="mt-1 text-sm text-domino-ink">
      <Link to={`/threads/${encodeURIComponent(account.subjectId)}`} className="font-semibold text-domino-aubergine underline">
        {account.name ?? account.domain ?? 'Compte'}
      </Link>
      <span className="text-domino-inkSoft"> · retrouvé dans le CRM par {account.by === 'crm_id' ? 'son ID CRM' : account.domain}</span>
    </p>
    <CrmAccountFacts account={crm.account} error={crm.error} onRetry={crm.reload} onSync={crm.syncFromCrm} syncing={crm.syncing} />
  </>;
};

/**
 * « Compte » on an object's page (F5f): the CRM account of the object, matched
 * by its CRM ID, else its exact domain (L48). Opening the page only reads; adding a company
 * to the CRM goes through the existing row action and its confirmation.
 */
export const AppRecordAccount = ({ recordId, refreshKey }: { recordId: string; refreshKey: string | null }) => {
  const [account, setAccount] = useState<Account | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    setAccount(null);
    setError(null);
    void readAppRecordAccount(recordId)
      .then((value) => { if (active) setAccount(value); })
      .catch((cause: unknown) => { if (active) setError(cause instanceof Error ? cause.message : 'Le compte lié n’a pas pu être chargé.'); });
    return () => { active = false; };
  }, [recordId, refreshKey, attempt]);

  // Nothing empty takes room: without a CRM, or outside it with nothing to do, the object says nothing about its account.
  const outside = account?.status === 'not_found' || account?.status === 'no_domain';
  const canAdd = outside && Boolean(account.runId && account.rowId);
  if (!error && (!account || account.status === 'no_crm' || (outside && !canAdd))) return null;

  return (
    <section aria-labelledby="app-record-account-title" className="rounded-2xl border border-domino-cardBorder bg-domino-white px-5 pt-4">
      <h2 id="app-record-account-title" className="text-sm font-semibold text-domino-ink">Compte</h2>
      {error && <p role="alert" className="py-3 text-sm text-red-700">
        {error} <button type="button" className="ml-1 underline" onClick={retry}>Réessayer</button>
      </p>}
      {account?.status === 'found' && <LinkedAccount account={account} />}
      {account?.status === 'ambiguous' && <p className="py-3 text-sm text-domino-inkSoft">
        {account.count} entreprises du CRM ont le domaine {account.domain}.
      </p>}
      {outside && account.runId && account.rowId && <div className="flex flex-wrap items-center gap-3 pb-4 pt-1 text-sm">
        <span className="font-semibold text-domino-ink">Hors CRM</span>
        <RowCrmAction sourceRunId={account.runId} rowId={account.rowId} rowLabel={account.rowLabel ?? 'cette entreprise'} className="" />
      </div>}
    </section>
  );
};
